import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";

// Registrar fuente Roboto
Font.register({
  family: "Roboto",
  fonts: [
    {
      src: "https://fonts.gstatic.com/s/roboto/v15/W5F8_SL0XFawnjxHGsZjJA.ttf",
    },
    {
      src: "https://fonts.gstatic.com/s/roboto/v15/bdHGHleUa-ndQCOrdpfxfw.ttf",
      fontWeight: 700,
    },
  ],
});

// Estilos
const styles = StyleSheet.create({
  page: { fontSize: 12, color: "#000", height: "100%", paddingBottom: 70 },
  header: {
    paddingHorizontal: 40,
    paddingTop: 20,
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  logo: { width: "100%", height: "15%" },
  watermark: {
    position: "absolute",
    top: "35%",
    left: "15%",
    width: "70%",
    opacity: 0.1,
    zIndex: -10,
  },
  title: {
    textAlign: "center",
    fontSize: 16,
    fontFamily: "Roboto",
    fontWeight: 700,
    marginBottom: 20,
  },
  text: {
    marginBottom: 5,
    fontSize: 10,
    fontFamily: "Roboto",
    fontWeight: 400,
    color: "#000",
  },
  textBold: { fontWeight: 700 },
  section: { paddingHorizontal: 40, marginBottom: 10 },
  sectionTitle: {
    fontSize: 12,
    fontFamily: "Roboto",
    fontWeight: 700,
    marginBottom: 5,
    marginTop: 10,
  },
  table: {
    width: "100%", // Cambiado a 100% para usar todo el ancho disponible
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0, // Mostrar borde derecho
    borderBottomWidth: 0, // Mostrar borde inferior
    /*  marginVertical: 10, */
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0,
    borderColor: "#000",
  },
  tableColHeader: {
    width: "33.33%", // Tres columnas iguales
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "#f0f0f0",
    paddingVertical: 5,
  },
  tableCol: {
    width: "33.33%", // Tres columnas iguales
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    paddingVertical: 5,
    paddingHorizontal: 3,
  },
  // Eliminamos los estilos de columnas wide y narrow ya que no son necesarios para 3 columnas
  tableCellHeader: {
    margin: 2,
    fontSize: 10,
    fontWeight: "bold",
    textAlign: "center", // Centrar texto en encabezados
  },
  tableCell: {
    margin: 2,
    fontSize: 10,
    textAlign: "center", // Centrar texto en celdas
  },
  firmas: {
    marginTop: 40,
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  firma: {
    width: 100,
    height: 60,
    marginBottom: 5,
    objectFit: "contain",
  },
  columDr: {
    textAlign: "center",
    alignItems: "center",
    justifyContent: "center",
    display: "flex",
  },
  gridItem: {
    width: "48%",
    marginBottom: 5,
  },
});

// Función para formatear fecha
const formatDateWithoutTimeZoneIssues = (isoString) => {
  // Dividir la fecha y hora para ignorar la zona horaria
  const [datePart] = isoString.split("T");
  const [year, month, day] = datePart.split("-");

  // Crear fecha local sin considerar la hora/UTC
  return new Date(year, month - 1, day).toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const VaccineOrderPdf = ({ data }) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Logo y marca de agua */}
        <Image
          style={styles.logo}
          src="/images/headerright.png" // Reemplaza con la ruta correcta
        />
        <Image
          style={styles.watermark}
          src="/images/longLogo.png" // Reemplaza con la ruta correcta
        />

        <Text style={styles.title}>ORDEN DE VACUNACIÓN</Text>

        <View style={styles.header}>
          <View style={styles.gridItem}>
            <Text style={styles.text}>
              <Text style={styles.textBold}>Paciente:</Text> {data.nombre}
            </Text>
            <Text style={styles.text}>
              <Text style={styles.textBold}>Fecha de nacimiento:</Text>{" "}
              {data.edad}
            </Text>
            <Text style={styles.text}>
              <Text style={styles.textBold}>Lugar de atención:</Text>{" "}
              {data.lugarAtencion}
            </Text>
          </View>
          <View style={styles.gridItem}>
            <Text style={styles.text}>
              <Text style={styles.textBold}>Seguro:</Text> {data.seguro}
            </Text>
            <Text style={styles.text}>
              <Text style={styles.textBold}>Diagnóstico:</Text>{" "}
              {data.diagnostico_dat || "No especificado"}
            </Text>
            <Text style={styles.text}>
              <Text style={styles.textBold}>Altura:</Text> {data.altura}cm
            </Text>
            <Text style={styles.text}>
              <Text style={styles.textBold}>Peso:</Text> {data.peso}kg
            </Text>
          </View>
        </View>
        <View style={{ marginBottom: 15 }}>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableCellHeader}>Vacuna</Text>
              </View>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableCellHeader}>Dosis</Text>
              </View>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableCellHeader}>Fecha</Text>
              </View>
            </View>

            {data.vacunas?.map((vacuna, index) => (
              <View key={index} style={styles.tableRow}>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>{vacuna.vacuna || "N/A"}</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>
                    {vacuna.tipo_dosis || "N/A"}
                  </Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>
                    {formatDateWithoutTimeZoneIssues(vacuna.fecha_aplicacion)}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.firmas}>
          <View style={styles.columDr}>
            <Image style={styles.firma} src="/images/firma.png" />
            <Text style={styles.text}>______________________</Text>
            <Text style={styles.text}>{data?.doctor?.printer_name}</Text>
            <Text style={styles.text}>{data?.doctor?.specialty}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default VaccineOrderPdf;
